import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { Loader2, ArrowRight, Zap, Search } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColor = (s) => {
  if (s === "completed") return "bg-[#4B6F44] text-white border-[#4B6F44]";
  if (s === "in_progress") return "bg-[#D6AD60]/20 text-[#5C4D24] border-[#D6AD60]";
  return "bg-transparent text-[#9F7D23] border-[#9F7D23]";
};

const filters = ["all", "pending", "in_progress", "completed"];

export default function OrdersList() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    setLoading(true);
    const params = filter !== "all" ? `?status=${filter}` : "";
    axios.get(`${API}/orders${params}`)
      .then((r) => setOrders(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [filter]);

  const filtered = orders.filter(
    (o) =>
      o.customer?.name?.toLowerCase().includes(search.toLowerCase()) ||
      o.order_number?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="orders-list-page">
      <div>
        <h2 className="font-['Space_Mono'] text-3xl tracking-tight uppercase text-[#5C4D24]">Orders</h2>
        <p className="text-sm text-[#8B7536] mt-1">Manage all customer orders</p>
      </div>

      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
        <div className="flex gap-1">
          {filters.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f}`}
              className={`px-4 py-2 font-['Space_Mono'] text-xs tracking-widest uppercase transition-all ${
                filter === f
                  ? "bg-[#9F7D23] text-white"
                  : "text-[#5C4D24] border border-[#D6D6CF] hover:border-[#9F7D23]"
              }`}
            >
              {f.replace("_", " ")}
            </button>
          ))}
        </div>
        <div className="relative w-full md:w-64">
          <Search className="w-4 h-4 absolute left-0 top-1/2 -translate-y-1/2 text-[#8B7536]" />
          <input
            type="text"
            placeholder="Search orders..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="search-orders-input"
            className="tinkah-input pl-6 text-sm"
          />
        </div>
      </div>

      <div className="bg-white border border-[#D6D6CF]" data-testid="orders-table">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-[#9F7D23] animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-[#8B7536] text-sm">No orders found</p>
          </div>
        ) : (
          <div>
            <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-[#D6D6CF] bg-[#F5F5F0]">
              <span className="col-span-2 font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536]">Order</span>
              <span className="col-span-3 font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536]">Customer</span>
              <span className="col-span-2 font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536]">Type</span>
              <span className="col-span-2 font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536]">Total</span>
              <span className="col-span-2 font-['Space_Mono'] text-[10px] tracking-widest uppercase text-[#8B7536]">Payment</span>
              <span className="col-span-1"></span>
            </div>
            {filtered.map((order) => (
              <Link
                key={order.id}
                to={`/orders/${order.id}`}
                data-testid={`order-link-${order.order_number}`}
                className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-[#D6D6CF] last:border-0 hover:bg-[#F5F5F0] hover:translate-x-1 transition-all group items-center"
              >
                <span className="col-span-2 font-['Space_Mono'] text-sm text-[#9F7D23]">{order.order_number}</span>
                <div className="col-span-3 text-sm truncate">
                  <span className="text-[#5C4D24]">{order.customer?.name}</span>
                  {order.customer?.profile_name && (
                    <span className="text-xs text-[#9F7D23] block">For: {order.customer.profile_name}</span>
                  )}
                </div>
                <span className="col-span-2 font-['Space_Mono'] text-xs uppercase text-[#8B7536]">
                  {order.items?.length || 0} items
                  {order.financial?.is_urgent && <Zap className="w-3 h-3 inline ml-1 text-[#9F3A3A]" />}
                </span>
                <span className="col-span-2 font-['Space_Mono'] text-sm text-[#5C4D24]">
                  {order.financial?.total_price?.toFixed(0)} AED
                </span>
                <span className="col-span-2">
                  {order.financial?.balance_due <= 0 ? (
                    <span className="font-['Space_Mono'] text-[10px] px-2 py-0.5 bg-[#4B6F44] text-white uppercase tracking-widest">Paid</span>
                  ) : (
                    <span className="font-['Space_Mono'] text-[10px] text-[#9F3A3A]">{order.financial?.balance_due} due</span>
                  )}
                </span>
                <span className="col-span-1 flex justify-end">
                  <ArrowRight className="w-4 h-4 text-[#8B7536] opacity-0 group-hover:opacity-100 transition-opacity" />
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
